/**
 * Mock Database Adapter
 * Implements IDatabasePort with in-memory data for development/testing
 *
 * This adapter allows the app to work without a real database.
 * Data is stored in memory and will reset on page refresh.
 */

import { Account } from '../../core/entities/Account'
import { Application } from '../../core/entities/Application'
import { Job, JobType, JobStatus } from '../../core/entities/Job'
import { Workflow } from '../../core/entities/Workflow'

// In-memory data store
const store = {
  accounts: [
    {
      id: 'acc-1',
      name: 'EVH Corporation',
      scoutAccountId: 'EVH',
      isDefault: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  ],
  applications: [
    {
      id: 'app-1',
      name: 'TestApp01',
      accountId: 'acc-1',
      scoutAccountId: 'EVH',
      sourceLanguage: 'cobol',
      targetLanguage: 'java',
      description: 'Customer Management System - COBOL to Java Migration',
      status: 'active',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  ],
  jobs: [
    {
      id: 'ingest_EVH_TestApp01_001',
      type: JobType.INGEST,
      status: JobStatus.COMPLETED,
      applicationId: 'app-1',
      scoutAccountId: 'EVH',
      applicationName: 'TestApp01',
      progress: 100,
      message: 'Ingest completed successfully',
      results: { filesProcessed: 3, totalLines: 850 },
      createdAt: new Date(Date.now() - 86400000).toISOString(),
      completedAt: new Date(Date.now() - 86400000 + 30000).toISOString(),
    },
    {
      id: 'ca_EVH_TestApp01_001',
      type: JobType.CODE_ANALYSIS,
      status: JobStatus.COMPLETED,
      applicationId: 'app-1',
      scoutAccountId: 'EVH',
      applicationName: 'TestApp01',
      progress: 100,
      message: 'Code analysis completed',
      results: { programsAnalyzed: 3, javaFilesGenerated: 3 },
      createdAt: new Date(Date.now() - 80000000).toISOString(),
      completedAt: new Date(Date.now() - 80000000 + 60000).toISOString(),
    },
  ],
  workflows: [],
  settings: {
    theme: 'dark',
    fontSize: 14,
    language: 'en',
    editorSettings: {
      tabSize: 4,
      wordWrap: true,
      minimap: true,
    },
  },
  awsCredentials: {
    accessKeyId: '',
    secretAccessKey: '',
    region: 'us-east-1',
  },
  aiProviders: {
    claude: { apiKey: '', model: 'claude-sonnet-4-20250514', enabled: false },
    openai: { apiKey: '', model: 'gpt-4o', enabled: false },
    bedrock: { enabled: false, region: 'us-east-1' },
  },
}

// Helper to generate IDs
const generateId = (prefix = '') => `${prefix}${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

// Helper to simulate async delay
const delay = (ms = 50) => new Promise(resolve => setTimeout(resolve, ms))

export class MockDatabaseAdapter {
  // ===== Accounts =====

  async getAccounts() {
    await delay()
    return store.accounts.map(a => new Account(a))
  }

  async getAccount(id) {
    await delay()
    const account = store.accounts.find(a => a.id === id)
    return account ? new Account(account) : null
  }

  async getDefaultAccount() {
    await delay()
    const account = store.accounts.find(a => a.isDefault)
    return account ? new Account(account) : null
  }

  async createAccount(data) {
    await delay()
    const account = {
      id: generateId('acc-'),
      name: data.name,
      scoutAccountId: data.scoutAccountId,
      isDefault: data.isDefault || false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    store.accounts.push(account)
    return new Account(account)
  }

  async updateAccount(id, data) {
    await delay()
    const index = store.accounts.findIndex(a => a.id === id)
    if (index === -1) throw new Error(`Account ${id} not found`)

    store.accounts[index] = {
      ...store.accounts[index],
      ...data,
      updatedAt: new Date().toISOString(),
    }
    return new Account(store.accounts[index])
  }

  async deleteAccount(id) {
    await delay()
    const index = store.accounts.findIndex(a => a.id === id)
    if (index !== -1) {
      store.accounts.splice(index, 1)
    }
  }

  // ===== Applications =====

  async getApplications(accountId) {
    await delay()
    let apps = store.applications
    if (accountId) {
      apps = apps.filter(a => a.accountId === accountId)
    }
    return apps.map(a => new Application(a))
  }

  async getApplication(id) {
    await delay()
    const app = store.applications.find(a => a.id === id)
    return app ? new Application(app) : null
  }

  async createApplication(data) {
    await delay()
    const app = {
      id: generateId('app-'),
      name: data.name,
      accountId: data.accountId,
      scoutAccountId: data.scoutAccountId,
      sourceLanguage: data.sourceLanguage || 'cobol',
      targetLanguage: data.targetLanguage || 'java',
      description: data.description || '',
      status: 'active',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    store.applications.push(app)
    return new Application(app)
  }

  async updateApplication(id, data) {
    await delay()
    const index = store.applications.findIndex(a => a.id === id)
    if (index === -1) throw new Error(`Application ${id} not found`)

    store.applications[index] = {
      ...store.applications[index],
      ...data,
      updatedAt: new Date().toISOString(),
    }
    return new Application(store.applications[index])
  }

  async deleteApplication(id) {
    await delay()
    const index = store.applications.findIndex(a => a.id === id)
    if (index !== -1) {
      store.applications.splice(index, 1)
    }
  }

  // ===== Jobs =====

  async getJobs(applicationId) {
    await delay()
    let jobs = store.jobs
    if (applicationId) {
      jobs = jobs.filter(j => j.applicationId === applicationId)
    }
    return jobs.map(j => new Job(j))
  }

  async getJob(id) {
    await delay()
    const job = store.jobs.find(j => j.id === id)
    return job ? new Job(job) : null
  }

  async createJob(data) {
    await delay()
    const job = {
      id: data.id || generateId(`${data.type}_`),
      type: data.type,
      status: JobStatus.PENDING,
      applicationId: data.applicationId,
      scoutAccountId: data.scoutAccountId,
      applicationName: data.applicationName,
      progress: 0,
      message: '',
      results: null,
      createdAt: new Date().toISOString(),
    }
    store.jobs.push(job)
    return new Job(job)
  }

  async updateJob(id, data) {
    await delay()
    const index = store.jobs.findIndex(j => j.id === id)
    if (index === -1) throw new Error(`Job ${id} not found`)

    store.jobs[index] = {
      ...store.jobs[index],
      ...data,
    }

    if (data.status === JobStatus.RUNNING && !store.jobs[index].startedAt) {
      store.jobs[index].startedAt = new Date().toISOString()
    }
    if (data.status === JobStatus.COMPLETED || data.status === JobStatus.FAILED) {
      store.jobs[index].completedAt = new Date().toISOString()
    }

    return new Job(store.jobs[index])
  }

  async deleteJob(id) {
    await delay()
    const index = store.jobs.findIndex(j => j.id === id)
    if (index !== -1) {
      store.jobs.splice(index, 1)
    }
  }

  // ===== Workflows =====

  async getWorkflows(applicationId) {
    await delay()
    let workflows = store.workflows
    if (applicationId) {
      workflows = workflows.filter(w => w.applicationId === applicationId)
    }
    return workflows.map(w => new Workflow(w))
  }

  async getWorkflow(id) {
    await delay()
    const workflow = store.workflows.find(w => w.id === id)
    return workflow ? new Workflow(workflow) : null
  }

  async createWorkflow(data) {
    await delay()
    const workflow = {
      id: generateId('wf-'),
      name: data.name,
      description: data.description || '',
      applicationId: data.applicationId,
      nodes: data.nodes || [],
      edges: data.edges || [],
      status: 'draft',
      version: 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    store.workflows.push(workflow)
    return new Workflow(workflow)
  }

  async updateWorkflow(id, data) {
    await delay()
    const index = store.workflows.findIndex(w => w.id === id)
    if (index === -1) throw new Error(`Workflow ${id} not found`)

    store.workflows[index] = {
      ...store.workflows[index],
      ...data,
      updatedAt: new Date().toISOString(),
    }
    return new Workflow(store.workflows[index])
  }

  async deleteWorkflow(id) {
    await delay()
    const index = store.workflows.findIndex(w => w.id === id)
    if (index !== -1) {
      store.workflows.splice(index, 1)
    }
  }

  // ===== Settings =====

  async getSettings() {
    await delay()
    return { ...store.settings }
  }

  async updateSettings(data) {
    await delay()
    store.settings = {
      ...store.settings,
      ...data,
    }
    return { ...store.settings }
  }

  async getAWSCredentials() {
    await delay()
    return { ...store.awsCredentials }
  }

  async updateAWSCredentials(data) {
    await delay()
    store.awsCredentials = {
      ...store.awsCredentials,
      ...data,
    }
    return { ...store.awsCredentials }
  }

  async getAIProvider(provider) {
    await delay()
    return store.aiProviders[provider] ? { ...store.aiProviders[provider] } : null
  }

  async updateAIProvider(provider, data) {
    await delay()
    store.aiProviders[provider] = {
      ...store.aiProviders[provider],
      ...data,
    }
    return { ...store.aiProviders[provider] }
  }
}

// Export singleton instance
export const mockDatabaseAdapter = new MockDatabaseAdapter()
